import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { audioUnmuteAsked, streamStarted } from 'features/streaming/actions';
import { screenshareStarted } from 'features/screenshare/actions';
import { RootState } from 'store/store';
import {
  LowerHandPayload,
  ParticipantsSortBy,
  RoleChangedPayload,
  User,
  UserId,
  UserLeftPayload,
  UserReactionAddedPayload,
  UsersState,
} from 'features/users/types';
import {
  avatarDeleteRequested,
  avatarUploadSucceeded,
  clearWaitingUsersList,
  signalingAvatarChanged,
  signalingAvatarDeleted,
  signalingHandLowered,
  signalingHandRaised,
  signalingUserJoined,
  signalingWaitingUserJoined,
  signalingWaitingUserLeft,
  signalingWaitingUserLeftBatch,
  signalingWaitingUsersUpdated,
  userRenamed,
} from 'features/users/actions';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import {
  signalingBroadcasterLimitReached,
  signalingRoomJoined,
  signalingScreenshareLimitReached,
} from 'features/room/actions';
import { PermissionTypes } from 'features/permissions/types';
import {
  dynamicBroadcastPermissionsReset,
  signalingBroadcastAllowed,
  signalingBroadcastDisallowed,
  signalingEditWhiteboardAllowed,
  signalingEditWhiteboardDisallowed,
  signalingPresentContentAllowed,
  signalingPresentContentDisallowed,
  signalingScreenshareAllowed,
  signalingScreenshareDisallowed,
} from 'features/permissions/actions';
import { userNameDecrypted } from 'features/e2ee/actions';
import { groupUserIdsByRole } from 'features/users/utils';
import { selectBreakoutOrUnassignedUser } from 'features/breakout-rooms/selectors/selectBreakoutOrUnassignedUser';
import { getEntriesByIds } from 'utils/getEntriesByIds';
import { removeDynamicBroadcastPermissions } from 'features/permissions/utils/removeDynamicBroadcastPermissions';
import {
  PRESENT_CONTENT_DYNAMIC_PERMISSIONS,
  removeDynamicPresentContentPermissions,
} from 'features/permissions/utils/removeDynamicPresentContentPermissions';
import { signalingBreakoutUserJoined } from 'features/breakout-rooms/actions';
import { selectPhoneUserById } from 'features/sip/sipSlice';

export const initialState: UsersState = {
  localUserName: '',
  localUserId: '',
  byId: {},
  availableIds: [],
  activeUsers: {},
  awaitingEntryIds: [],
  relistedWaitingUsers: {},
  broadcastLimitedIds: [],
  screenshareLimitedIds: [],
  handRaisedIds: [],
  participantsSortBy: 'firstName',
};

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    userLeft(state, action: PayloadAction<UserLeftPayload[]>) {
      const availableIdsSet = new Set(state.availableIds);
      const handRaisedIdsSet = new Set(state.handRaisedIds);

      for (const { userId } of action.payload) {
        availableIdsSet.delete(userId);
        handRaisedIdsSet.delete(userId);
        delete state.activeUsers[userId];
      }

      state.availableIds = Array.from(availableIdsSet);
      state.handRaisedIds = Array.from(handRaisedIdsSet);
    },
    raiseHand(state) {
      state.byId[state.localUserId].handRaised = true;
      state.handRaisedIds.push(state.localUserId);
    },
    lowerHand(state, action: PayloadAction<LowerHandPayload>) {
      // only handle local user
      if (action.payload === undefined) {
        state.byId[state.localUserId].handRaised = false;
      }
    },
    handRaiseDismissed(state, action: PayloadAction<UserId>) {
      state.byId[action.payload].handRaised = false;
      state.handRaisedIds = state.handRaisedIds.filter((id) => id !== action.payload);
    },
    changeRole(state, action: PayloadAction<RoleChangedPayload>) {
      state.byId[action.payload.id].role = action.payload.role;
    },
    broadcastLimitedDismissed(state) {
      state.broadcastLimitedIds = [];
    },
    screenshareLimitedDismissed(state) {
      state.screenshareLimitedIds = [];
    },
    userListReset(state) {
      const { localUserId } = state;

      state.availableIds = [localUserId];

      state.byId = {
        [localUserId]: state.byId[localUserId],
      };

      state.activeUsers = {
        [localUserId]: state.activeUsers[localUserId],
      };
    },
    userReactionAdded(state, action: PayloadAction<UserReactionAddedPayload>) {
      const { userId, reaction } = action.payload;
      const user = state.byId[userId];

      if (user) {
        user.reaction = reaction;
      }
    },
    userReactionRemoved(state, action: PayloadAction<UserId>) {
      const user = state.byId[action.payload];

      if (user) {
        user.reaction = undefined;
      }
    },
    userReactionReset(state) {
      const { localUserId } = state;

      const user = state.byId[localUserId];

      if (user) {
        user.reaction = undefined;
      }
    },
    participantsSortByChanged(state, action: PayloadAction<ParticipantsSortBy>) {
      state.participantsSortBy = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signalingUserJoined, (state, action) => {
        const user = action.payload;
        const knownUser = state.byId[user.id];

        if (knownUser) {
          if (knownUser.name) {
            // re-assign user name in e2ee scenario
            user.name = knownUser.name;
          }
          // move the waiting user to a room
          state.awaitingEntryIds = state.awaitingEntryIds.filter((id) => id !== user.id);
          delete state.relistedWaitingUsers[user.id];
        }

        state.availableIds.push(user.id);
        state.availableIds = [...new Set(state.availableIds)];

        state.byId[user.id] = user;
        state.activeUsers[user.id] = true;
      })
      .addCase(signalingRoomJoined, (state, action) => {
        const { id: localUserId } = action.payload.user;

        // override the data for the local user received in the prejoin call
        const localUser = {
          ...(state.byId[localUserId] || {}),
          ...action.payload.user,
          name: state.localUserName,
          dynamicPermissions: action.payload.user.dynamicPermissions,
          local: true,
        };

        state.byId = {
          [localUserId]: localUser,
        };
        state.localUserId = localUserId;

        state.availableIds = [localUserId];
        state.activeUsers = { [localUserId]: true };
        state.handRaisedIds = localUser.handRaised ? [localUser.id] : [];

        for (const user of action.payload.users) {
          state.byId[user.id] = user;
          state.availableIds.push(user.id);
          state.activeUsers[user.id] = true;

          if (user.handRaised) {
            state.handRaisedIds.push(user.id);
          }
        }

        state.awaitingEntryIds = [];
        state.relistedWaitingUsers = {};
        for (const user of action.payload.usersAwaitingEntry) {
          state.byId[user.id] = user;
          state.awaitingEntryIds.push(user.id);
        }
      })
      .addCase(authorizeUser.fulfilled, (state, action) => {
        const { name: username, initials } = action.meta;

        const localUser = {
          ...action.payload.user,
          name: username,
          local: true,
          initials,
        };

        state.localUserName = username;
        state.localUserId = localUser.id;
        state.byId[localUser.id] = localUser;
        state.availableIds.push(localUser.id);
        state.activeUsers[localUser.id] = true;
      })
      .addCase(userNameDecrypted, (state, action) => {
        const { id, name, rename } = action.payload;

        const user = state.byId[id];
        if (user) {
          user.name = name;

          if (rename) {
            user.initials = undefined;
          }
        }
      })
      .addCase(signalingHandRaised, (state, action) => {
        const { id } = action.payload.initiator;
        const user = state.byId[id];

        if (user) {
          user.handRaised = true;
          state.handRaisedIds.push(id);
          state.handRaisedIds = [...new Set(state.handRaisedIds)];
        }
      })
      .addCase(signalingHandLowered, (state, action) => {
        const user = state.byId[action.payload.id];

        if (user) {
          user.handRaised = false;
        }

        state.handRaisedIds = state.handRaisedIds.filter((id) => action.payload.id !== id);
      })
      .addCase(signalingBroadcastAllowed, (state, action) => {
        for (const { id, audio, video } of action.payload.data.users) {
          const user = state.byId[id];
          if (user) {
            user.dynamicPermissions ??= [];

            if (audio) {
              user.dynamicPermissions.push(PermissionTypes.broadcastAudio);
            }

            if (video) {
              user.dynamicPermissions.push(PermissionTypes.broadcastVideo);
            }
          }
        }
      })
      .addCase(signalingBroadcastDisallowed, (state, action) => {
        for (const id of action.payload.data.users) {
          const user = state.byId[id];
          if (user?.dynamicPermissions) {
            user.dynamicPermissions = removeDynamicBroadcastPermissions(user.dynamicPermissions);
          }
        }
      })
      .addCase(dynamicBroadcastPermissionsReset, (state, action) => {
        const user = state.byId[action.payload.id];

        if (user?.dynamicPermissions) {
          user.dynamicPermissions = removeDynamicBroadcastPermissions(user.dynamicPermissions);
        }
      })
      .addCase(signalingScreenshareAllowed, (state, action) => {
        for (const id of action.payload.data.users) {
          const user = state.byId[id];
          if (user) {
            user.dynamicPermissions ??= [];
            user.dynamicPermissions.push(PermissionTypes.screenshare);
          }
        }
      })
      .addCase(signalingScreenshareDisallowed, (state, action) => {
        for (const id of action.payload.data.users) {
          const user = state.byId[id];
          if (user?.dynamicPermissions) {
            user.dynamicPermissions = user.dynamicPermissions.filter(
              (permission) => permission !== PermissionTypes.screenshare
            );
          }
        }
      })
      .addCase(signalingEditWhiteboardAllowed, (state, action) => {
        for (const id of action.payload.data.users) {
          const user = state.byId[id];
          if (user) {
            user.dynamicPermissions ??= [];
            user.dynamicPermissions.push(PermissionTypes.editWhiteboard);
          }
        }
      })
      .addCase(signalingEditWhiteboardDisallowed, (state, action) => {
        for (const id of action.payload.data.users) {
          const user = state.byId[id];
          if (user?.dynamicPermissions) {
            user.dynamicPermissions = user.dynamicPermissions.filter(
              (permission) => permission !== PermissionTypes.editWhiteboard
            );
          }
        }
      })
      .addCase(signalingPresentContentAllowed, (state, action) => {
        for (const id of action.payload.data.users) {
          const user = state.byId[id];
          if (user) {
            user.dynamicPermissions ??= [];

            for (const permission of PRESENT_CONTENT_DYNAMIC_PERMISSIONS) {
              if (!user.dynamicPermissions.includes(permission)) {
                user.dynamicPermissions.push(permission);
              }
            }
          }
        }
      })
      .addCase(signalingPresentContentDisallowed, (state, action) => {
        for (const id of action.payload.data.users) {
          const user = state.byId[id];
          if (user?.dynamicPermissions) {
            user.dynamicPermissions = removeDynamicPresentContentPermissions(
              user.dynamicPermissions
            );
          }
        }
      })
      .addCase(signalingWaitingUserJoined, (state, action) => {
        state.byId[action.payload.id] = action.payload;

        state.awaitingEntryIds.push(action.payload.id);
        state.awaitingEntryIds = [...new Set(state.awaitingEntryIds)];

        // still active here means a re-list of someone in the room: reducers run before the
        // saga that clears activeUsers for them
        if (state.activeUsers[action.payload.id]) {
          state.relistedWaitingUsers[action.payload.id] = true;
        }
      })
      .addCase(signalingWaitingUserLeft, (state, action) => {
        state.awaitingEntryIds = state.awaitingEntryIds.filter((id) => id !== action.payload.id);
        delete state.byId[action.payload.id];
        delete state.relistedWaitingUsers[action.payload.id];
      })
      .addCase(signalingWaitingUserLeftBatch, (state, action) => {
        const awaitingEntryIdsSet = new Set(state.awaitingEntryIds);

        for (const { id } of action.payload.users) {
          awaitingEntryIdsSet.delete(id);
          delete state.byId[id];
          delete state.relistedWaitingUsers[id];
        }

        state.awaitingEntryIds = Array.from(awaitingEntryIdsSet);
      })
      .addCase(clearWaitingUsersList, (state) => {
        for (const id of state.awaitingEntryIds) {
          delete state.byId[id];
        }
        state.awaitingEntryIds = [];
        state.relistedWaitingUsers = {};
      })
      .addCase(signalingWaitingUsersUpdated, (state, action) => {
        for (const user of action.payload) {
          state.byId[user.id] = user;
          state.awaitingEntryIds.push(user.id);
        }
        state.awaitingEntryIds = [...new Set(state.awaitingEntryIds)];
      })
      .addCase(signalingBroadcasterLimitReached, (state, action) => {
        if (action.payload.id) {
          state.broadcastLimitedIds.push(action.payload.id);
          state.broadcastLimitedIds = [...new Set(state.broadcastLimitedIds)];
        }
      })
      .addCase(streamStarted, (state, action) => {
        state.broadcastLimitedIds = state.broadcastLimitedIds.filter(
          (id) => id !== action.payload.id
        );

        const user = state.byId[action.payload.id];
        if (user?.askedToUnmuteAudio && action.payload.streams.some((s) => s.type === 'audio')) {
          user.askedToUnmuteAudio = false;
        }
      })
      .addCase(signalingScreenshareLimitReached, (state, action) => {
        if (action.payload.id) {
          state.screenshareLimitedIds.push(action.payload.id);
          state.screenshareLimitedIds = [...new Set(state.screenshareLimitedIds)];
        }
      })
      .addCase(screenshareStarted, (state, action) => {
        state.screenshareLimitedIds = state.screenshareLimitedIds.filter(
          (id) => id !== action.payload.id
        );
      })
      .addCase(signalingBreakoutUserJoined, (state, action) => {
        const { user } = action.payload;
        const knownUser = state.byId[user.id];

        if (knownUser) {
          // remove the user from waiting list when they join a breakout room
          state.awaitingEntryIds = state.awaitingEntryIds.filter((id) => id !== user.id);
          delete state.relistedWaitingUsers[user.id];
        }
      })
      .addCase(userRenamed, (state, action) => {
        const { id, name } = action.payload;

        const user = state.byId[id];
        if (user) {
          user.name = name;
          user.initials = undefined;

          if (state.localUserId === id) {
            state.localUserName = name;
          }
        }
      })
      .addCase(signalingAvatarChanged, (state, action) => {
        const { url } = action.payload.data;
        const userId = action.payload.initiator.id;
        if (state.byId[userId]) {
          state.byId[userId].avatarUrl = url;
        }
      })
      .addCase(avatarUploadSucceeded, (state, action) => {
        if (state.byId[state.localUserId]) {
          state.byId[state.localUserId].avatarUrl = action.payload.url;
        }
      })
      .addCase(avatarDeleteRequested, (state) => {
        if (state.byId[state.localUserId]) {
          state.byId[state.localUserId].avatarUrl = undefined;
        }
      })
      .addCase(signalingAvatarDeleted, (state, action) => {
        const { userId } = action.payload;
        if (state.byId[userId]) {
          state.byId[userId].avatarUrl = undefined;
        }
      })
      .addCase(audioUnmuteAsked, (state, action) => {
        const { data } = action.payload;

        if (data) {
          const user = state.byId[data.id];
          if (user) {
            user.askedToUnmuteAudio = true;
          }
        }
      });
  },
});

export const {
  userLeft,
  raiseHand,
  lowerHand,
  changeRole,
  handRaiseDismissed,
  broadcastLimitedDismissed,
  screenshareLimitedDismissed,
  userListReset,
  userReactionAdded,
  userReactionRemoved,
  userReactionReset,
  participantsSortByChanged,
} = usersSlice.actions;

export default usersSlice.reducer;

export const selectLocalUserId = (state: RootState) => state.users.localUserId;

export const selectUserEntries = (state: RootState) => state.users.byId;

export const selectLocalUser = (state: RootState): User | undefined =>
  state.users.byId[state.users.localUserId];
export const selectLocalUserRole = (state: RootState) =>
  state.users.byId[state.users.localUserId].role;

export const selectUserById = createSelector(
  [selectUserEntries, (state: RootState, id: UserId) => id],
  (byId, id) => byId[id]
);

export const selectGlobalUser = createSelector(
  [selectUserById, selectBreakoutOrUnassignedUser, selectPhoneUserById],
  (user, breakoutUser, phoneUser) => user || breakoutUser || phoneUser
);

export const selectAvailableUserIds = (state: RootState) => state.users.availableIds;

export const selectActiveUsers = (state: RootState) => state.users.activeUsers;

export const selectAvailableUserIdsWithoutLocal = createSelector(
  [selectAvailableUserIds, selectLocalUserId],
  (availableIds, localId) => availableIds.filter((id) => id !== localId)
);

export const selectActiveUserById = createSelector(
  [
    selectUserEntries,
    (state: RootState) => state.users.activeUsers,
    (state: RootState, id: UserId) => id,
  ],
  (byId, activeUsers, id) => {
    if (activeUsers[id]) {
      return byId[id];
    }

    return undefined;
  }
);

export const selectUsers = createSelector(
  [selectAvailableUserIds, selectUserEntries],
  (availableIds, entries) => getEntriesByIds(availableIds, entries)
);

export const selectUsersWithoutLocal = createSelector(
  [selectUsers, selectLocalUserId],
  (entries, localId) => Object.values(entries).filter((user) => user.id !== localId)
);

export const selectAllIdsByRole = createSelector(
  [selectAvailableUserIds, selectUserEntries],
  (availableIds, entries) => groupUserIdsByRole(availableIds, entries)
);

export const selectLocalUserHandRaised = (state: RootState) =>
  state.users.byId[state.users.localUserId].handRaised;

export const selectAwaitingEntryIds = (state: RootState) => state.users.awaitingEntryIds;

// waiting entries a userLeft for a never-active id may evict; re-listed ids are excluded because
// their old room session can still emit a trailing userLeft
export const selectEvictableAwaitingEntryIds = createSelector(
  [selectAwaitingEntryIds, (state: RootState) => state.users.relistedWaitingUsers],
  (awaitingEntryIds, relistedWaitingUsers) =>
    awaitingEntryIds.filter((id) => !relistedWaitingUsers[id])
);

export const selectBroadcastLimitedIds = (state: RootState) => state.users.broadcastLimitedIds;

export const selectScreenshareLimitedIds = (state: RootState) => state.users.screenshareLimitedIds;

export const selectRemoteUsersByRole = createSelector(
  [selectUsersWithoutLocal, (state: RootState, role: string) => role],
  (remoteUsers, role) => remoteUsers.filter((user) => user.role === role)
);

export const selectHandRaisedIds = (state: RootState) => state.users.handRaisedIds;

export const selectParticipantsSortBy = (state: RootState) => state.users.participantsSortBy;
